const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const INITIAL_ALBUM_FORM = {
  titre: "",
  description: "",
  is_active: true,
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const truncateText = (value = "", limit = 140) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();

  if (text.length <= limit) return text;

  return `${text.slice(0, limit).trim()}...`;
};

export const getAlbumDate = (album) =>
  album?.createdAt || album?.created_at || album?.updatedAt || album?.updated_at;

export const getAlbumPhotos = (album) => {
  if (Array.isArray(album?.photos)) return album.photos;
  if (Array.isArray(album?.Photos)) return album.Photos;
  return [];
};

export const getCoverPhoto = (album) => {
  const photos = getAlbumPhotos(album);
  return photos[0]?.photo_url || "";
};

export const getStatusLabel = (isActive) => (isActive ? "Actif" : "Inactif");

export const getStatusClass = (isActive) =>
  isActive
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

export const filterAndSortAlbums = (albums = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const status = filters.status || "all";

  let result = [...albums];

  if (status === "active") {
    result = result.filter((album) => Boolean(album.is_active));
  }

  if (status === "inactive") {
    result = result.filter((album) => !Boolean(album.is_active));
  }

  if (query) {
    result = result.filter((album) => {
      const haystack = normalizeText([album.titre, album.description].join(" "));
      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "created_asc") {
      return new Date(getAlbumDate(a) || 0) - new Date(getAlbumDate(b) || 0);
    }

    if (filters.sortOrder === "title_asc") {
      return normalizeText(a.titre).localeCompare(normalizeText(b.titre));
    }

    if (filters.sortOrder === "title_desc") {
      return normalizeText(b.titre).localeCompare(normalizeText(a.titre));
    }

    if (filters.sortOrder === "photos_desc") {
      return getAlbumPhotos(b).length - getAlbumPhotos(a).length;
    }

    return new Date(getAlbumDate(b) || 0) - new Date(getAlbumDate(a) || 0);
  });

  return result;
};

export const buildAlbumsStats = (albums = [], filteredAlbums = []) => {
  const active = albums.filter((album) => Boolean(album.is_active)).length;
  const inactive = albums.length - active;

  const photos = albums.reduce(
    (total, album) => total + getAlbumPhotos(album).length,
    0
  );

  const emptyAlbums = albums.filter(
    (album) => getAlbumPhotos(album).length === 0
  ).length;

  return {
    total: albums.length,
    filtered: filteredAlbums.length,
    active,
    inactive,
    photos,
    emptyAlbums,
  };
};

export const normalizeAlbumPayload = (formData) => ({
  ...formData,
  titre: String(formData.titre || "").trim(),
  description: String(formData.description || "").trim(),
  is_active: Boolean(formData.is_active),
});

export const validateAlbumForm = (formData) => {
  if (!String(formData.titre || "").trim()) {
    return "Le titre de l'album est obligatoire.";
  }

  return null;
};

export const buildInitialFormFromAlbum = (album) => {
  if (!album) return INITIAL_ALBUM_FORM;

  return {
    ...INITIAL_ALBUM_FORM,
    ...album,
    is_active: Boolean(album.is_active),
  };
};

export const normalizePhotoPayload = (formData) => ({
  ...formData,
  gallerie_id: formData.gallerie_id,
  photo_url: String(formData.photo_url || "").trim(),
  description: String(formData.description || "").trim(),
  is_active:
    formData.is_active === null || formData.is_active === undefined
      ? true
      : Boolean(formData.is_active),
});